import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CountryCode } from '../types';
import { COUNTRY_CODES } from '../src/config/countryCodes';
import { filterCountries, getCountryDisplayName } from '../src/utils/phoneUtils';

interface CountryCodeSelectProps {
  value: string;
  onChange: (dialCode: string) => void;
  hasError?: boolean;
  className?: string;
}

const CountryCodeSelect: React.FC<CountryCodeSelectProps> = ({ value, onChange, hasError, className = '' }) => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // 获取当前选中的国家
  const selectedCountry = COUNTRY_CODES.find(c => c.dialCode === value) || COUNTRY_CODES[0];
  
  // 过滤后的国家列表
  const filteredCountries = filterCountries(COUNTRY_CODES, searchQuery);

  // 点击外部关闭下拉
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // 展开时自动聚焦搜索框
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = useCallback(() => {
    setIsOpen(prev => !prev);
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  const handleSelect = useCallback((country: CountryCode) => {
    onChange(country.dialCode);
    setIsOpen(false);
    setSearchQuery('');
  }, [onChange]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      {/* 触发器按钮 */}
      <button
        type="button"
        onClick={handleToggle}
        className={`flex items-center justify-between gap-1.5 bg-slate-50 border ${
          hasError ? 'border-red-400' : 'border-slate-100'
        } rounded-xl px-3 py-3.5 text-slate-900 font-medium hover:bg-white focus:bg-white focus:ring-4 focus:ring-blue-500/10 outline-none transition-all cursor-pointer min-w-[100px] ${className}`}
      >
        <span className="text-base">{selectedCountry.dialCode}</span>
        <svg
          className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* 下拉列表 */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-200 rounded-xl shadow-xl z-50 overflow-hidden">
          {/* 搜索框 */}
          <div className="p-2 border-b border-slate-100">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder={t('countryCodeSelect.searchPlaceholder', '搜索国家或区号')}
              className="w-full bg-slate-50 border border-slate-100 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
            />
          </div>

          {/* 国家列表 */}
          <div ref={listRef} className="max-h-[300px] overflow-y-auto">
            {filteredCountries.length > 0 ? (
              filteredCountries.map(country => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleSelect(country)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-slate-50 transition-colors cursor-pointer ${
                    country.dialCode === value ? 'bg-blue-50' : ''
                  }`}
                >
                  <span className="text-slate-900 font-medium flex-1 truncate">
                    {getCountryDisplayName(country, i18n.language)}
                  </span>
                  <span className="text-slate-500 text-sm font-mono">{country.dialCode}</span>
                </button>
              ))
            ) : (
              <div className="px-3 py-4 text-center text-slate-400 text-sm">
                {t('countryCodeSelect.noResults', '无匹配结果')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(CountryCodeSelect);
